import { IUserPermissions } from '../../Components/sharedTypes';
import { IApplication } from '../types';

type IntegrationConfig = {
  permissionKey?: string; // Optional permission key for the integration
  newIntegrationEnabled?: boolean; // Indicates if the integration is enabled
  applicationKey?: string; // Optional application key (e.g., linked to an application)
};

type IntegrationMap = Record<string, IntegrationConfig>;

/**
 * Get all integration configurations.
 * @param enabled Indicates whether new integrations should be enabled.
 * @returns A map of available integrations.
 */
const integrations = (enabled: boolean): IntegrationMap => ({
  revolve: {
    permissionKey: 'Export To Revolve Onboard'
  },
  scoutOnboard: {
    permissionKey: 'Export To SCOUT Onboard'
  },
  xref: {
    permissionKey: 'Xref (Export)',
    applicationKey: 'xref'
  },
  mvs: {
    newIntegrationEnabled: enabled,
    permissionKey: 'My Verification Service (MVS)',
    applicationKey: 'mvs'
  },
  referoo: {
    permissionKey: 'Referoo',
    applicationKey: 'referoo'
  },
  foundU: {
    newIntegrationEnabled: enabled,
    permissionKey: 'foundU',
    applicationKey: 'foundu'
  },
  testGrid: {
    permissionKey: 'TestGrid',
    applicationKey: 'test_grid'
  },
  hro: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Export To HR Onboard'
  },
  cognology: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Export To Cognology',
    applicationKey: 'cognology'
  },
  roubler: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Roubler Integration',
    applicationKey: 'roubler'
  },
  sparkhire: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Export To Sparkhire',
    applicationKey: 'sparkhire'
  },
  fit2work: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Fit2Work',
    applicationKey: 'fit2_work'
  },
  alayacare: {
    newIntegrationEnabled: enabled,
    permissionKey: 'AlayaCare Integration',
    applicationKey: 'alayacare'
  },
  elmo: {
    newIntegrationEnabled: enabled,
    permissionKey: 'Elmo Integration',
    applicationKey: 'elmo'
  },
  intellihr: {
    newIntegrationEnabled: enabled,
    permissionKey: 'IntelliHR',
    applicationKey: 'intellihr'
  }
});

/**
 * Check if an integration is available based on the provided parameters.
 * @param integrationName The name of the integration to check.
 * @param newIntegrationEnabled Whether new integrations are enabled.
 * @param permissions The user's permissions object.
 * @param application The application data object.
 * @returns `true` if the integration is available.
 */
const isIntegrationAvailable = (
  integrationName: keyof IntegrationMap,
  newIntegrationEnabled: boolean,
  permissions: IUserPermissions | undefined,
  application: IApplication | undefined
): boolean => {
  const integrationConfig = integrations(newIntegrationEnabled)[integrationName];

  if (!integrationConfig) {
    console.warn(`Integration not found: ${integrationName}`);
    return false;
  }

  const hasPermission = integrationConfig.permissionKey
    ? (permissions?.Integrations?.[integrationConfig.permissionKey] ?? false)
    : true;

  const isEnabled = integrationConfig.newIntegrationEnabled ?? true;

  const hasApplicationKey = integrationConfig.applicationKey
    ? !!application?.integrations?.[integrationConfig.applicationKey]
    : true;

  return hasPermission && isEnabled && hasApplicationKey;
};

export { integrations, isIntegrationAvailable };
